import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface PreviewProperty {
  id: string
  name: string
}

interface ReportPreview {
  html: string
  subject: string | null
}

interface UserReportPreviewCardProps {
  userId: string
  properties: PreviewProperty[]
  reportingMode?: string
}

/**
 * Generate the owner report email on behalf of a user, on the user detail page,
 * without sending it. A user can own several properties (the report is sent per
 * property), so this picks from the user's assigned properties and renders the
 * selected one's report HTML in a sandboxed iframe.
 *
 * The preview is generated on behalf of the user: it passes the user id so the
 * backend renders net vs gross per the user's reporting_mode. Nothing is sent —
 * the actual report-send path is unchanged (always gross for now).
 */
export function UserReportPreviewCard({
  userId,
  properties,
  reportingMode,
}: UserReportPreviewCardProps) {
  const { toast } = useToast()
  const list = useMemo(() => (Array.isArray(properties) ? properties : []), [properties])
  const [selectedId, setSelectedId] = useState(list[0]?.id ?? '')
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<ReportPreview | null>(null)
  const requestedRef = useRef<string | null>(null)

  // Keep the selection valid: default to the first property, and reset if the
  // current one is removed from the list (e.g. unchecked in Property access) so we
  // never preview a property that's no longer selectable.
  useEffect(() => {
    if (list.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (selectedId !== '') setSelectedId('')
    } else if (!list.some(p => p.id === selectedId)) {
      setSelectedId(list[0].id)
    }
  }, [list, selectedId])
  useEffect(() => {
    // Clear stale HTML and invalidate any in-flight request whenever an input that
    // affects the preview changes — property, user, or the user's reporting mode —
    // so a previous response can't render under the new context (e.g. a gross
    // preview lingering after the user is flipped to net).
    requestedRef.current = `${selectedId}|${userId}|${reportingMode}`
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreview(null)
    setLoading(false)
  }, [selectedId, userId, reportingMode])

  const generate = async () => {
    if (!selectedId) return
    const requested = `${selectedId}|${userId}|${reportingMode}`
    requestedRef.current = requested
    setLoading(true)
    try {
      const result = await api.getReportEmailPreview(selectedId, userId)
      if (requestedRef.current !== requested) return // inputs changed mid-request
      setPreview({ html: result?.html || '', subject: result?.subject || null })
    } catch (err: unknown) {
      if (requestedRef.current !== requested) return
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to generate preview',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      if (requestedRef.current === requested) setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report preview</CardTitle>
        <CardDescription>
          Render the owner report email for one of this user&apos;s properties, on behalf of this
          user — nothing is sent. Reflects the user&apos;s reporting mode (currently{' '}
          <span className="text-text">{reportingMode === 'net' ? 'net (owner net)' : 'gross'}</span>
          ). The actual report owners receive is unchanged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.length === 0 ? (
          <div className="text-sm text-textMuted">
            This user has no assigned properties to preview a report for.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label htmlFor="preview-property" className="text-xs font-medium text-textMuted">
                  Property
                </label>
                <select
                  id="preview-property"
                  value={selectedId}
                  disabled={loading}
                  onChange={e => setSelectedId(e.target.value)}
                  className="flex h-8 w-72 max-w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {list.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="button" size="sm" disabled={loading || !selectedId} onClick={generate}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Generating…
                  </>
                ) : preview ? (
                  'Regenerate preview'
                ) : (
                  'Generate preview'
                )}
              </Button>
            </div>
            {preview && (
              <div className="space-y-2">
                {preview.subject && (
                  <div className="text-sm text-text">
                    <span className="font-medium text-textMuted">Subject:</span> {preview.subject}
                  </div>
                )}
                <iframe
                  title="Report email preview"
                  sandbox=""
                  srcDoc={preview.html}
                  className="h-[600px] w-full rounded-input border border-border bg-white"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
